// release-manifest-hash.ts
//
// B2-1 (design H14 bản 2, 2026-09-29): what a G3 approval is bound to. The
// hash used to be `${missionId}-${branch}` — constant for the whole mission —
// so a G3 approved while NO deploy recipe existed stayed valid after one was
// added (or after the smoke argv changed), and the first `run` afterwards
// executed a side effect nobody had looked at. It is now the sha256 of the
// canonical release manifest: the candidate plus every `config.release`
// field that decides what the deploy/verify leg executes. An absent recipe is
// `null` (never ""), so "added a recipe" and "changed a recipe" both move the
// hash, and the G3 artifact hash (`<candidate>:<manifest>`) moves with it.
//
// `gitlab_base_url` is deliberately NOT part of it: it only says where the
// GitLab API lives for the Draft-MR evidence, it runs nothing.
//
// A4 (close-out 2026-09-30): `deploy_timeout_sec` / `verify_timeout_sec` ARE
// part of it — how long a deploy may run is part of how it deploys. Unlike the
// recipes they enter ONLY when set: an absent timeout adds no key, so a config
// written before A4 keeps the exact hash its G3 approval and deploy receipt
// were bound to (upgrading the CLI must not re-request G3 or re-deploy).
import { canonicalJson, sha256Checksum } from "./canonical-json-and-checksum.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";

export type ReleaseConfig = NonNullable<AutopilotConfig["release"]>;

type TimeoutKey = "deploy_timeout_sec" | "verify_timeout_sec";

/** The timeout keys that are SET — absent ones contribute nothing (see header). */
function timeoutKeys(
  release: ReleaseConfig,
  keys: TimeoutKey[],
): Partial<Record<TimeoutKey, number>> {
  const out: Partial<Record<TimeoutKey, number>> = {};
  for (const key of keys) {
    const value = release[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

export function computeReleaseManifestHash(candidateSha: string, release: ReleaseConfig): string {
  return sha256Checksum(
    canonicalJson({
      schema_version: 1,
      candidate_sha: candidateSha,
      remote: release.remote,
      target_branch: release.target_branch,
      deploy_recipe: release.deploy_recipe ?? null,
      verify_recipe: release.verify_recipe ?? null,
      ...timeoutKeys(release, ["deploy_timeout_sec", "verify_timeout_sec"]),
    }),
  );
}

/**
 * Review follow-up M1 (2026-09-29): what the DEPLOY step is bound to —
 * candidate + remote + target + deploy_recipe, without verify_recipe. The
 * deploy receipt keys on this, so fixing only the smoke recipe (a new
 * manifest ⇒ a new G3) never re-runs a deploy that already succeeded.
 */
export function computeReleaseDeployHash(candidateSha: string, release: ReleaseConfig): string {
  return sha256Checksum(
    canonicalJson({
      schema_version: 1,
      candidate_sha: candidateSha,
      remote: release.remote,
      target_branch: release.target_branch,
      deploy_recipe: release.deploy_recipe ?? null,
      ...timeoutKeys(release, ["deploy_timeout_sec"]),
    }),
  );
}

/** The G3 `artifact_hash` shape shared by request, recheck and approval. */
export function g3ArtifactHash(candidateSha: string, releaseManifestHash: string): string {
  return `${candidateSha}:${releaseManifestHash}`;
}
